import { useState, useEffect } from "react";
import placeholderImage from "../../assets/images/naturesweden.jpg";

/**
 * Custom hook for managing map state and area data
 * Consolidates multiple useEffects for area management, images, and species
 * 
 * @param {Array} areas - Areas data from API
 * @param {Object} usePolygonHook - usePolygon hook instance
 * @returns {Object} Map state and handlers
 */
export function useMapState(areas, usePolygonHook) {
    const [areaSpecies, setFilteredSpecies] = useState([]);
    const [filteredAreas, setFilteredAreas] = useState([]);
    const [selectedArea, setSelectedArea] = useState(null);
    const [mapCenter, setMapCenter] = useState({ lat: 62.0, lng: 15.0 });
    const [mapZoom, setMapZoom] = useState(4);
    const [pinsUrl, setPinsUrl] = useState("");
    const [pins, setPins] = useState([]);
    const [hasCentered, setHasCentered] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [selectedSpecies, setSelectedSpecies] = useState(null);
    const [images, setImages] = useState({});
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);

    const { updatePolygon, updateCenter } = usePolygonHook;

    // Fetch and manage area images
    useEffect(() => {
        if (areas) {
            const fetchImages = async () => {
                const imagePromises = areas.map(async (area) => {
                    try {
                        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/areas/${area.id}/images`);
                        if (!response.ok) {
                            return { id: area.id, url: placeholderImage };
                        }
                        const blob = await response.blob();
                        return { id: area.id, url: URL.createObjectURL(blob) };
                    } catch (error) {
                        console.error("Error fetching image:", error);
                        return { id: area.id, url: placeholderImage };
                    }
                });

                const imageData = await Promise.all(imagePromises);
                const imageMap = imageData.reduce((acc, img) => {
                    acc[img.id] = img.url;
                    return acc;
                }, {});
                setImages(imageMap);
            };
            
            fetchImages();
        }
    }, [areas]);

    // Initialize filtered areas
    useEffect(() => {
        if (areas) {
            setFilteredAreas(areas);
        }
    }, [areas]);

    // Center map on pins when they're fetched
    useEffect(() => {
        if (selectedArea && pins.length > 0 && !hasCentered) {
            const selectedAreaData = areas?.find((area) => area.name === selectedArea);

            if (selectedAreaData) {
                pins.sort((a, b) => a.pin_order - b.pin_order);
                updatePolygon(pins);

                const latitudes = pins.map((pin) => pin.latitude);
                const longitudes = pins.map((pin) => pin.longitude);

                const centerLatitude =
                    latitudes.reduce((sum, lat) => sum + lat, 0) / latitudes.length;
                const centerLongitude =
                    longitudes.reduce((sum, lng) => sum + lng, 0) / longitudes.length;

                updateCenter({ lat: centerLatitude, lng: centerLongitude });

                setHasCentered(true);
            }
        }
    }, [selectedArea, pins, areas, updatePolygon, updateCenter, hasCentered]);

    // Reset pins when area changes
    useEffect(() => {
        if (selectedArea) {
            setPins([]);
            setHasCentered(false);
        }
    }, [selectedArea]);

    const handleView = (area) => {
        setSelectedArea(area.name);
        setPinsUrl(`${process.env.REACT_APP_API_URL}/api/pins/${area.id}`);

        // Fetch species for the selected area
        fetch(`${process.env.REACT_APP_API_URL}/api/species/${area.id}`)
            .then((response) => {
                if (!response.ok) {
                    console.warn(`No species found for area ${area.id}`);
                    return [];
                }
                return response.json();
            })
            .then((data) => {
                setFilteredSpecies(data || []);
            })
            .catch((error) => {
                console.error("Error fetching species:", error);
                setFilteredSpecies([]);
            });
    };

    const toggleSpeciesDescription = (species) => {
        setSelectedSpecies(species);
        setDialogOpen(true);
    };

    const handleDialogClose = () => {
        setDialogOpen(false);
        setSelectedSpecies(null);
    };

    const updatePinsFromFetch = (fetchedPins) => {
        if (fetchedPins && fetchedPins.length > 0) {
            const firstPin = fetchedPins[0];
            if (firstPin.latitude && firstPin.longitude) {
                setMapCenter({ lat: firstPin.latitude, lng: firstPin.longitude });
                setMapZoom(14);
            }
            setPins(fetchedPins);
        }
    };

    return {
        areaSpecies,
        filteredAreas,
        setFilteredAreas,
        selectedArea,
        mapCenter,
        mapZoom,
        pinsUrl,
        pins,
        dialogOpen,
        selectedSpecies,
        images,
        isSidebarOpen,
        setIsSidebarOpen,
        handleView,
        toggleSpeciesDescription,
        handleDialogClose,
        updatePinsFromFetch,
    };
}
